// Invitación al equipo por correo (CC-CONSOLE#1512).
//
// La liga que recibe el invitado es `/register?invite=<token>`. El token viaja
// al backend en /auth/register (como `inviteToken`) para que su usuario nazca
// dentro de la compañía que lo invitó. Se guarda en sessionStorage porque el flujo cruza
// recargas, es de un solo uso y se borra en cuanto el backend contesta.
//
// Es una credencial: quien la tenga (y controle una cuenta con el correo
// invitado) entra a la compañía. Por eso se quita de la URL en cuanto se
// guarda, y eso pasa en el ARRANQUE (utils/teamInviteBoot, primer import de
// main.tsx), antes de que Sentry o PostHog lean `window.location`. Aquí nunca
// se escribe en consola.
//
// Sin dependencias a propósito: tiene que poder correr antes que todo lo demás.

const KEY = "teamInviteToken";
const PARAM = "invite";

// Respaldo si sessionStorage no está disponible (modo privado estricto).
let memory: string | null = null;

// Misma forma que valida el backend (userInvitationsService.isPlausibleToken).
const TOKEN_RE = /^[A-Za-z0-9_-]{32,200}$/;

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** Token de `?invite=` de la URL actual, o null. */
export const inviteTokenFromUrl = (search: string = window.location.search): string | null => {
  const token = new URLSearchParams(search).get(PARAM);
  return token && TOKEN_RE.test(token) ? token : null;
};

/** Guarda el token de la URL (si lo hay) para el resto del flujo. */
export const captureInviteToken = (search?: string): string | null => {
  const token = inviteTokenFromUrl(search);
  if (token) {
    memory = token;
    try {
      storage()?.setItem(KEY, token);
    } catch {
      /* sin almacenamiento: queda el respaldo en memoria */
    }
  }
  return token;
};

/**
 * Guarda el token de la URL actual y lo QUITA de la barra de direcciones (sin
 * recargar ni dejar entrada en el historial). Nunca lanza: corre antes de
 * montar la app.
 */
export const captureAndStripInviteToken = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(PARAM)) return null;
    const token = captureInviteToken(window.location.search);
    // Se quita aunque no tenga forma de token: tampoco debe quedarse a la vista.
    params.delete(PARAM);
    const query = params.toString();
    const clean = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(window.history.state, "", clean);
    return token;
  } catch {
    return null;
  }
};

/** El token vigente: el de la URL manda sobre el guardado. */
export const getInviteToken = (search?: string): string | undefined => {
  const fromUrl = captureInviteToken(search);
  if (fromUrl) return fromUrl;
  try {
    const stored = storage()?.getItem(KEY);
    if (stored && TOKEN_RE.test(stored)) return stored;
  } catch {
    /* sin almacenamiento: queda el respaldo en memoria */
  }
  return memory ?? undefined;
};

/** Ya se usó (o ya no sirve): que no se reenvíe en el siguiente intento. */
export const clearInviteToken = (): void => {
  memory = null;
  try {
    storage()?.removeItem(KEY);
  } catch {
    /* nada que limpiar */
  }
};
